<template>
  <q-layout view="hHh lpR fFf">
    <q-header
      data-test-id="consumer-header"
      class="shadow-1"
      :class="headerClass"
    >
      <q-toolbar
        class="justify-end"
        style="margin-left: auto; margin-right: auto;"
        :style="{
          'max-width': $getMaxWidthForScreenSize(),
          'padding': $q.screen.gt.sm ? '0' : undefined
        }"
      >
        <q-toolbar-title
          v-if="$q.screen.gt.xs"
          class="col-auto cursor-pointer"
        >
          <div
            class="col"
            @click="currentSite.navigateToLogoRoute($router)"
          >
            <img
              alt="Logo"
              :src="$isColorDark(currentSite.headerColor) ? lightLogoImageUrl : darkLogoImageUrl"
              class="title-logo q-mt-md q-mb-sm"
              :class="{
                'title-logo-small': !$q.screen.gt.sm,
                'q-ml-md': $q.screen.gt.sm
              }"
              :style="{
                'max-width': $q.screen.lt.sm ? '30vw' : undefined,
                'max-height': $q.screen.lt.sm ? '50px' : undefined,
                'height': $q.screen.lt.sm ? 'auto' : undefined
              }"
            >
          </div>
        </q-toolbar-title>
        <q-btn
          flat
          :label="$q.screen.gt.xs ? 'Home' : undefined"
          :icon="$q.screen.gt.xs ? 'fal fa-angle-left' : 'fal fa-arrow-left'"
          :dense="$q.screen.xs"
          @click="back()"
        />
        <template v-if="$q.screen.xs">
          <q-space />
          <div
            class="text-h5 text-capitalize"
            :class="paddingClass"
          >
            {{ title }}
          </div>
        </template>
        <q-space />

        <language-selector
          v-if="currentCircuit.supportsTranslations"
          :type="'button-dropdown'"
          dense
          class="col-auto"
          :text-color="textColor"
          :label-style="'flag-only'"
        />
        <q-btn
          v-if="currentSite.showCart && $order.draftOrderItemsCartCount"
          flat
          dense
          round
          icon="fal fa-cart-shopping"
          @click="checkout()"
        >
          <q-badge
            :label="$order.draftOrderItemsCartCount"
            color="red"
            floating
          />
        </q-btn>

        <q-btn
          v-if="$q.screen.gt.xs && currentUser"
          :class="$isColorDark(currentSite.headerColor) ? 'text-white' : 'text-negative'"
          flat
          :label="$t('auth.actions.logout')"
          icon-right="fal fa-arrow-right-from-bracket"
          to="/logout"
        />
      </q-toolbar>
    </q-header>
    <login-sign-up-card
      v-if="!currentUser"
      :post-auth-path="'/account'"
      class="column justify-center items-center self-center fixed-full"
    />
    <template v-else>
      <q-drawer
        v-if="$q.screen.gt.xs"
        show-if-above
        side="left"
        :width="$q.screen.gt.md ? 500 : 320"
        :breakpoint="600"
        bordered
        class="row justify-center bg-responsive-white"
      >
        <q-scroll-area class="fit">
          <account-navigation
            :class="$q.screen.gt.md ? 'q-ml-xl q-mt-xl' : 'q-mt-lg'"
          >
            <template #greeting-message>
              <div
                class="col-auto text-left text-h5 q-pa-md"
                data-test-id="user-greeting"
              >
                👋 {{ $t('common.nameGreeting', { name: currentUser.displayFirstName }) }}
              </div>
            </template>
          </account-navigation>
        </q-scroll-area>
      </q-drawer>

      <q-page-container>
        <q-page
          class="flex flex-center q-py-none"
        >
          <router-view
            ref="routerView"
          />
        </q-page>
      </q-page-container>
    </template>
  </q-layout>
</template>

<script>
import { Dark } from 'quasar'
import { darkLogoImageUrl, lightLogoImageUrl } from 'src/config'
import AccountNavigation from 'src/modules/consumer/components/account/AccountNavigation'
import LoginSignUpCard from 'src/modules/consumer/components/account/LoginSignUpCard'
import LanguageSelector from 'src/modules/consumer/components/LanguageSelector'
export default {
  name: 'AccountLayout',
  components: {
    LanguageSelector,
    AccountNavigation,
    LoginSignUpCard
  },
  context: ['currentUser'],
  configuration: ['currentSite', 'currentCircuit', 'navigations', 'voucherTypes', 'sites'],
  data () {
    return {
      deleteAccountOkLabel: this.$i18n.t('user.actions.deleteAccount'),
      deleteAccountOkDisable: true,
      deleteAccountOkLoading: false,
      title: this.$t('pages.account.myAccount')
    }
  },
  models: {
    orders: {
      orderItems: {
        ticket: {
          showing: 'default'
        }
      },
      force: true,
      variables () {
        return {
          state: 'not_draft',
          limit: 10,
          userId: this.currentUser?.id,
          noCount: true
        }
      }
    }
  },
  computed: {
    headerClass () {
      if (this.$q.screen.xs) {
        return this.$isColorDark(this.currentSite.primaryColor) ? 'bg-primary text-white' : 'bg-primary text-black'
      }
      return this.$isColorDark(this.currentSite.headerColor) ? 'bg-header text-white' : 'bg-header text-dark'
    },
    textColor () {
      if (this.$q.screen.xs) {
        return this.$isColorDark(this.currentSite.primaryColor) ? 'text-white' : 'text-black'
      }
      return this.$isColorDark(this.currentSite.headerColor) ? 'text-white' : 'text-dark'
    },
    paddingClass () {
      // add padding to make sure the title is in the middle of the header
      if (this.$q.screen.gt.xs) return
      if (this.currentSite.showCart && this.$order.draftOrderItemsCartCount && this.currentCircuit.supportsTranslations) return 'q-pl-lg'
      if ((this.currentSite.showCart && this.$order.draftOrderItemsCartCount) || this.currentCircuit.supportsTranslations) return
      return 'q-pr-lg'
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler: async function (to, from) {
        const routeTitles = {
          'consumer:my-membership': this.$tc('membership.membership', 1),
          'consumer:account-information': this.$t('pages.account.accountInformation'),
          'consumer:orders': this.$t('order.orderHistory'),
          'consumer:manage-friends': this.$t('user.actions.manageFriends'),
          'consumer:my-movies': this.$t('movie.myMovies'),
          'consumer:manage-members': this.$tc('membership.actions.manageMember', 2),
          'consumer:update-payment-method': this.$t('payment.paymentInformation'),
          'consumer:manage-marketing-preferences': this.$t('user.marketingPreferences')
        }

        // Ignore any middle :site-${id}: namespace (e.g. consumer:site-1:account-information )
        const nameParts = _.split(to.name, ':') || []
        const topNamespace = _.first(nameParts) || ''
        const routeName = _.last(nameParts) || ''
        this.title = routeTitles[`${topNamespace}:${routeName}`] || this.$t('pages.account.myAccount')
      }
    }
  },
  meta () {
    return {
      title: this.title
    }
  },
  created () {
    _.extend(this, { darkLogoImageUrl, lightLogoImageUrl })

    this.setThemeAndStyle()
  },
  methods: {
    setThemeAndStyle () {
      if (!this.currentSite) return

      this.$mh.setThemeColors(this.currentSite)

      this.$mh.setSiteStyles(this.currentSite.transformedStyles)

      // For Dark Mode toggle
      if (this.$consumer) {
        Dark.set(this.currentSite.consumerConfig.darkMode)
      }
    },
    back () {
      if (this.$q.screen.gt.xs) {
        this.currentSite.navigateToLogoRoute(this.$router)
      } else {
        this.$router.back()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
 .title-logo {
    height: 65px;
  }
  .title-logo-small {
    height: 45px;
  }
</style>
